import React, { useEffect, useState } from 'react';
import { useData } from '../../hooks/useData';
import { useToast } from '../../hooks/useToast';
import { projectTrackingServices } from '../../APIServices/projectTrackingServices';
import { usersManagementServices } from '../../APIServices/usersManagementServices';

function TaskCreateModal({ open, onClose, studyId, onCreated, milestoneOptions = [] }) {
  const { currentUser } = useData();
  const toast = useToast();
  const [buttonLoader, setButtonLoader] = useState(false);
  const [ownerList, setOwnerList] = useState([]);
  const [form, setForm] = useState({ 
    title: '', 
    projectMilestoneId: '',
    projectMilestoneName: '',
    owner: '', 
    dueAt: '', 
    priority: 'medium', 
    status: 'todo' 
  });

  const resetForm = () => {
    setForm({ 
      title: '', 
      projectMilestoneId: '',
      projectMilestoneName: '',
      owner: '', 
      dueAt: '', 
      priority: 'medium', 
      status: 'todo' 
    });
  };

  const getOwnerValue = (user) => user?.user || user?.userId || user?.id || '';
  const getOwnerLabel = (user) => {
    const fullName = [user?.firstName, user?.middleName, user?.lastName].filter(Boolean).join(' ').trim();
    return fullName || user?.name || user?.email || 'Unknown User';
  };

  const loadOwners = async () => {
    try {
      let data = await usersManagementServices.getUserData('all', 'user', '', 1, 1000);
      let users = data?.data || [];
      setOwnerList(users);
    } catch (error) {
      setOwnerList([]);
      toast('Unable to load owner list.', 'warn');
    }
  };

  useEffect(() => {
    if (open) {
      loadOwners();
    }
  }, [open]);

  const submit = () => {
    if (!form.title) {
      toast('Title is required', 'warn');
      return;
    }
    createTask();
  };

  const createTask = async () => {
    setButtonLoader(true);
    try {
      await projectTrackingServices.addNewProjectTracking({
        recordType: 'task',
        studyId,
        title: form.title,
        projectMilestoneId: form.projectMilestoneId,
        projectMilestoneName: form.projectMilestoneName,
        owner: form.owner || currentUser?.id || currentUser?.userId || currentUser?.user,
        dueAt: form.dueAt ? new Date(form.dueAt).toISOString() : null,
        priority: form.priority,
        status: form.status,
      });
      toast('Task created', 'ok');
      resetForm();
      onClose();
      onCreated?.();
    } catch (error) {
      toast('Unable to create task.', 'warn');
    } finally {
      setButtonLoader(false);
    }
  };

  if (!open) return null;

  return (
    <div className="modal-backdrop" onClick={(e) => {
      if (e.target === e.currentTarget) onClose();
    }}>
      <div className="modal">
        <div className="modal-head">
          <div>
            <div className="modal-title">New task</div>
            <div className="xs muted">Add a task to track operational items.</div>
          </div>
          <button className="icon-btn" onClick={onClose}>
            <i className="ti ti-x" />
          </button>
        </div>

        <div className="modal-body">
          <div className="field">
            <label className="field-label">Task title <span className="req">*</span></label>
            <input 
              value={form.title} 
              onChange={(e) => setForm({ ...form, title: e.target.value })} 
              placeholder="e.g. Complete linearity validation runs"
              autoFocus
            />
          </div>

          <div className="field-row">
            <div className="field">
              <label className="field-label">Linked milestone</label>
              <select
                value={form.projectMilestoneId}
                onChange={(e) => {
                  const selected = milestoneOptions.find(
                    (item) => String(item.projectMilestoneId || item._id) === String(e.target.value)
                  );

                  setForm({
                    ...form,
                    projectMilestoneId: e.target.value,
                    projectMilestoneName: selected?.projectMilestone || '',
                  });
                }}
              >
                <option value="">Not linked</option>
                {milestoneOptions.map((item) => (
                  <option key={item._id || item.projectMilestoneId} value={item.projectMilestoneId || item._id}>
                    {item.projectMilestone}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label className="field-label">Owner</label>
              <select 
                value={form.owner} 
                onChange={(e) => setForm({ ...form, owner: e.target.value })}
              >
                <option value="">Select Owner</option>
                {ownerList.map((u) => (
                  <option key={getOwnerValue(u)} value={getOwnerValue(u)}>{getOwnerLabel(u)}</option>
                ))}
              </select>
            </div>
            <div className="field">
              <label className="field-label">Due date</label>
              <input 
                type="date" 
                value={form.dueAt} 
                onChange={(e) => setForm({ ...form, dueAt: e.target.value })} 
              />
            </div>
          </div>

          <div className="field-row">
            <div className="field">
              <label className="field-label">Priority</label>
              <select 
                value={form.priority} 
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div className="field">
              <label className="field-label">Status</label>
              <select 
                value={form.status} 
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                <option value="todo">Not started</option>
                <option value="in_progress">In progress</option>
                <option value="blocked">Blocked</option>
                <option value="done">Done</option>
              </select>
            </div>
          </div>
        </div>

        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={() => { resetForm(); onClose(); }}>Cancel</button>
          {buttonLoader ? (
            <button className="btn btn-primary" disabled><div className='buttonLoader' /> Create task</button>
          ) : (
            <button className="btn btn-primary" onClick={submit}>
              <i className="ti ti-check" /> Create task
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default TaskCreateModal;